import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import VendorLayout from '@/components/layouts/VendorLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  FileText, Plus, Clock, CheckCircle, XCircle,
  Loader2, Search, Eye
} from 'lucide-react';
import { getApiUrl } from '@/lib/api';

interface PitchRow {
  id: string;
  product_name?: string;
  category?: string;
  status_v2?: string;
  view_count?: number;
  outcome?: string;
  ai_score?: number;
  created_at?: string;
}

const STATUS_BADGE: Record<string, { label: string; color: string }> = {
  DRAFT: { label: 'Draft', color: 'bg-zinc-500/10 text-zinc-400' },
  SUBMITTED: { label: 'Submitted', color: 'bg-rose-500/10 text-rose-400' },
  AI_EVALUATED: { label: 'Evaluated', color: 'bg-blue-500/10 text-blue-400' },
  VALUABLE: { label: 'Valuable', color: 'bg-emerald-500/10 text-emerald-400' },
  DECLINED: { label: 'Declined', color: 'bg-rose-500/10 text-rose-400' },
  DELIVERED: { label: 'Delivered', color: 'bg-blue-500/10 text-blue-400' },
  ACCEPTED: { label: 'Accepted', color: 'bg-emerald-500/10 text-emerald-400' },
  REJECTED: { label: 'Rejected', color: 'bg-rose-500/10 text-rose-400' },
};

export default function VendorPitchesPage() {
  const { token } = useAuth();
  const [pitches, setPitches] = useState<PitchRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => { fetchPitches(); }, []);

  const fetchPitches = async () => {
    try {
      const res = await fetch(getApiUrl('/api/v2/vendor/pitches-with-metrics?limit=100'), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setPitches(await res.json());
    } catch (err) {
      console.error('Failed to load pitches:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredPitches = pitches.filter((p) => {
    const matchesSearch = p.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.category && p.category.toLowerCase().includes(searchTerm.toLowerCase()));
    if (filterStatus === 'all') return matchesSearch;
    return matchesSearch && p.status_v2 === filterStatus;
  });

  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const filterOptions = [
    { value: 'all', label: 'All' },
    { value: 'DRAFT', label: 'Draft' },
    { value: 'SUBMITTED', label: 'Submitted' },
    { value: 'AI_EVALUATED', label: 'Evaluated' },
    { value: 'VALUABLE', label: 'Valuable' },
    { value: 'DELIVERED', label: 'Delivered' },
    { value: 'ACCEPTED', label: 'Accepted' },
    { value: 'REJECTED', label: 'Rejected' },
  ];

  return (
    <VendorLayout>
      <div className="space-y-6" data-testid="vendor-pitches-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white">My Pitches</h1>
            <p className="text-zinc-500 mt-1">Track and manage your submitted pitches</p>
          </div>
          <Link to="/vendor/pitches/new">
            <Button className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="new-pitch-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Pitch
            </Button>
          </Link>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              placeholder="Search pitches..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-11 bg-[#18181b] border-white/10 text-white placeholder-zinc-500"
              data-testid="search-pitches"
            />
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {filterOptions.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setFilterStatus(opt.value)}
                className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                  filterStatus === opt.value
                    ? 'bg-rose-500 text-white'
                    : 'bg-[#18181b] border border-white/5 text-zinc-400 hover:bg-white/5'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-[#18181b] rounded-xl border border-white/5 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-zinc-600 mx-auto" />
            </div>
          ) : filteredPitches.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-400 font-medium">No pitches found</p>
              <p className="text-zinc-600 text-sm mt-1">Submit your first pitch to get started</p>
              <Link to="/vendor/pitches/new">
                <Button className="mt-4 bg-rose-600 hover:bg-rose-700 text-white">
                  <Plus className="w-4 h-4 mr-2" /> Submit Pitch
                </Button>
              </Link>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-zinc-500 text-xs uppercase tracking-wider border-b border-white/5">
                    <th className="text-left py-3 px-4 font-medium">Pitch</th>
                    <th className="text-left py-3 px-3 font-medium">Status</th>
                    <th className="text-center py-3 px-3 font-medium">Views</th>
                    <th className="text-left py-3 px-3 font-medium">Outcome</th>
                    <th className="text-center py-3 px-3 font-medium">Score</th>
                    <th className="text-right py-3 px-4 font-medium">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {filteredPitches.map((p) => {
                    const badge = STATUS_BADGE[p.status_v2 ?? ''] ?? STATUS_BADGE.DRAFT;
                    return (
                      <tr key={p.id} className="hover:bg-white/5 transition-colors" data-testid={`pitch-row-${p.id}`}>
                        <td className="py-3 px-4">
                          <Link to={`/vendor/pitches/${p.id}`} className="font-medium text-white hover:text-rose-300 truncate block max-w-[220px]">
                            {p.product_name}
                          </Link>
                          {p.category && <p className="text-xs text-zinc-600 mt-0.5">{p.category}</p>}
                        </td>
                        <td className="py-3 px-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
                            {badge.label}
                          </span>
                        </td>
                        <td className="py-3 px-3 text-center">
                          <span className="inline-flex items-center gap-1 text-zinc-300">
                            <Eye className="w-3 h-3 text-zinc-600" />
                            {p.view_count}
                          </span>
                        </td>
                        <td className="py-3 px-3">
                          {p.outcome ? (
                            <span className={`inline-flex items-center gap-1 text-xs font-medium ${
                              p.outcome === 'Accepted' ? 'text-emerald-400' :
                              p.outcome === 'Rejected' ? 'text-rose-400' :
                              'text-zinc-500'
                            }`}>
                              {p.outcome === 'Accepted' && <CheckCircle className="w-3 h-3" />}
                              {p.outcome === 'Rejected' && <XCircle className="w-3 h-3" />}
                              {p.outcome === 'Pending' && <Clock className="w-3 h-3" />}
                              {p.outcome}
                            </span>
                          ) : (
                            <span className="text-xs text-zinc-600">—</span>
                          )}
                        </td>
                        <td className="py-3 px-3 text-center">
                          {p.ai_score != null ? (
                            <span className={`font-medium ${p.ai_score >= 70 ? 'text-emerald-400' : p.ai_score >= 50 ? 'text-blue-400' : 'text-zinc-400'}`}>
                              {Math.round(p.ai_score)}
                            </span>
                          ) : (
                            <span className="text-zinc-600">—</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-right text-xs text-zinc-500">{formatDate(p.created_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </VendorLayout>
  );
}
